import React, { useState } from "react";
import {
  Calendar,
  Clock,
  DollarSign,
  BookOpen,
  Users,
  Star,
  X,
  User,
  Mail,
  Phone,
  Briefcase,
  GraduationCap,
  Send,
  Check,
} from "lucide-react";

const CoursesPage = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    education: "",

    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const courses = [
    {
      id: 1,
      name: "MERN Stack Development",
      description:
        "Complete MERN (MongoDB, Express.js, React.js, Node.js) stack development course for building modern web applications.",
      duration: "4 months",
      schedule: "Monday - Friday",
      timing: "9:00 AM - 1:00 PM",
      price: "₹45,000",
      features: [
        "Full-stack JavaScript development",
        "Real-world project experience",
        "Industry-standard tools and practices",
        "Job placement assistance",
      ],
    },
    {
      id: 2,
      name: "DevOps Engineering",
      description:
        "Comprehensive DevOps course covering CI/CD, containerization, cloud platforms, and automation tools.",
      duration: "3 months",
      schedule: "Monday - Friday",
      timing: "2:00 PM - 6:00 PM",
      price: "₹50,000",
      features: [
        "AWS/Azure cloud platforms",
        "Docker and Kubernetes",
        "Jenkins and GitLab CI",
        "Infrastructure as Code",
      ],
    },
    {
      id: 3,
      name: "Software Testing",
      description:
        "Complete software testing course covering manual and automation testing methodologies.",
      duration: "3 months",
      schedule: "Monday - Friday",
      timing: "10:00 AM - 2:00 PM",
      price: "₹35,000",
      features: [
        "Manual testing fundamentals",
        "Selenium automation",
        "API testing",
        "Performance testing",
      ],
    },
    {
      id: 4,
      name: "Python Full Stack Development",
      description:
        "Comprehensive Python programming course covering web development with Django and Flask.",
      duration: "4 months",
      schedule: "Monday - Friday",
      timing: "3:00 PM - 7:00 PM",
      price: "₹40,000",
      features: [
        "Python programming fundamentals",
        "Django web framework",
        "Database management",
        "RESTful API development",
      ],
    },
  ];

  const handleApply = (course) => {
    setSelectedCourse(course);
    setShowModal(true);
    setError("");
    setSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Simulate API call - replace with your actual API endpoint
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Reset form and show success message
      setSuccess(true);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        education: "",

        message: "",
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError("Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const Modal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slideDown">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-[#24786D]">
                Apply for {selectedCourse?.name}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg">
                {error}
              </div>
            )}

            {success ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Application Submitted Successfully!
                </h3>
                <p className="text-gray-600">
                  We'll get back to you soon with next steps.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      Education
                    </label>
                    <input
                      type="text"
                      name="education"
                      value={formData.education}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message (Optional)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Tell us why you're interested in this course..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex items-center justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center gap-2 bg-[#24786D] text-white px-6 py-2 rounded-lg hover:bg-[#1a5a52] transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#24786D] mb-4 animate-fade-in">
            Professional Training Courses
          </h1>
          <p className="mt-2 text-gray-600 text-lg md:text-xl">
            Advance your career with our industry-focused training programs
          </p>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="p-6 md:p-8">
                <h2 className="text-2xl font-semibold text-[#24786D] mb-3">
                  {course.name}
                </h2>

                <p className="text-gray-600 mb-6">{course.description}</p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-5 h-5 mr-3 text-[#24786D]" />
                    <span>Duration: {course.duration}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-5 h-5 mr-3 text-[#24786D]" />
                    <span>Timing: {course.timing}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <BookOpen className="w-5 h-5 mr-3 text-[#24786D]" />
                    <span>Schedule: {course.schedule}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <DollarSign className="w-5 h-5 mr-3 text-[#24786D]" />
                    <span>Price: {course.price}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                    <Star className="w-5 h-5 mr-2 text-[#24786D]" />
                    Key Features
                  </h3>
                  <ul className="space-y-2">
                    {course.features.map((feature, index) => (
                      <li
                        key={index}
                        className="text-gray-600 flex items-start"
                      >
                        <span className="text-[#24786D] mr-2">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleApply(course)}
                  className="w-full bg-[#24786D] text-white py-3 px-6 rounded-lg hover:bg-[#1a5a52] transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Users className="w-5 h-5" />
                  Apply Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Modal />
    </div>
  );
};

export default CoursesPage;
